import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { PayrollConfigService } from 'src/app/services/payroll-config.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

const PERIODICITY_OPTIONS = ['Semanal', 'Catorcenal', 'Quincenal', 'Mensual'];
const CALCULATION_TYPE_OPTIONS = ['Diario', 'Semanal', 'Quincenal', 'Mensual'];
const ISR_MINIMUM_WAGE_OPTIONS = ['Sin retención', 'Con retención'];
const BIMONTHLY_CALENDAR_OPTIONS = [1, 2, 3, 4, 5, 6];
const DISCOUNT_TYPE_OPTIONS = ['Falta injustificada', 'Incapacidad', 'Vacaciones', 'Permiso'];

export type PayrollConfigTab = 'generales' | 'cuotas_imss' | 'retencion_imss' | 'credito_infonavit';

@Component({
    selector: 'app-payroll-config',
    templateUrl: './payroll-config.component.html',
    styleUrls: ['./payroll-config.component.css']
})
export class PayrollConfigComponent implements OnInit {

    readonly periodicityOptions = PERIODICITY_OPTIONS;
    readonly calculationTypeOptions = CALCULATION_TYPE_OPTIONS;
    readonly isrMinimumWageOptions = ISR_MINIMUM_WAGE_OPTIONS;
    readonly weekDaysOptions = [1, 2, 3, 4, 5, 6, 7];
    readonly bimonthlyCalendarOptions = BIMONTHLY_CALENDAR_OPTIONS;
    readonly discountTypeOptions = DISCOUNT_TYPE_OPTIONS;

    activeTab: PayrollConfigTab = 'generales';

    /** Filas de la tabla "Cuotas de IMSS" (solo lectura, valores fijados por ley). */
    imssRatesUngrouped: any[] = [];
    imssRatesGrouped: any[] = [];
    imssGroupLabel = '';
    imssGroupExpanded = true;

    /** Estado de los 3 desplegables de "Configurar faltas por tipo de descuento". */
    openDropdown: string | null = null;

    form!: FormGroup;

    constructor(
        private fb: FormBuilder,
        private payrollConfigService: PayrollConfigService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) {
        this.form = this.fb.group({
            payroll_number: [null],
            periodicity: [''],
            payment_days: [null],

            first_payment_date: [''],
            days_per_year: [null],
            week_days: [null],

            calculation_type: [''],

            minimum_wage: [null],
            uma_daily: [null],
            work_shift_hours: [null],

            employment_subsidy_enabled: [false],
            uma_monthly: [null],
            monthly_quota: [null],
            income_limit: [null],

            isr_month: [null],
            isr_year: [null],
            integrated_daily_salary: [null],
            vacation_days: [null],

            monthly_factor_calculation: ['days_of_year'],
            isr_minimum_wage: [''],
            local_tax_limit_percentage: [null],

            social_prevision_uma_limit: [null],
            isr_includes_limit: [false],

            breakdown_vacation_payment: [false],
            automatic_vacation_payment: [false],
            anniversary_completion: [{ value: false, disabled: true }],
            complementary_expiration: [{ value: false, disabled: true }],

            // Retención IMSS
            sdi_calculation_source: ['manual_sdi'],
            imss_retention_validation_base: ['daily_salary'],
            sdi_variable_days_basis: ['calendar_days'],
            discount_absences_sickness_maternity: [true],

            // Crédito INFONAVIT
            infonavit_umi: [null],
            infonavit_bimonthly_calendar: [null],
            housing_damage_insurance_amount: [null],
            infonavit_min_wage_validation: ['daily_salary'],
            infonavit_discount_by_absence_type_enabled: [true],
            infonavit_absence_percentage_types: [[]],
            infonavit_absence_fixed_amount_types: [[]],
            infonavit_absence_fixed_sm_types: [[]],
        });
    }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Parámetros de nómina', action);
    }

    ngOnInit(): void {
        this.load();
        this.loadImssRates();
    }

    load(): void {
        this.spinner.show();
        this.payrollConfigService.get().subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.form.patchValue({
                    ...resp,
                    first_payment_date: resp?.first_payment_date ? String(resp.first_payment_date).split('T')[0] : '',
                });
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar la configuración de nómina', icon: 'error' });
            }
        });
    }

    setTab(tab: PayrollConfigTab): void {
        this.activeTab = tab;
    }

    private loadImssRates(): void {
        this.payrollConfigService.getImssQuotaRates().subscribe({
            next: (rows: any[]) => {
                const all = rows || [];
                this.imssRatesUngrouped = all.filter(r => !r.group_label);
                this.imssRatesGrouped = all.filter(r => r.group_label);
                this.imssGroupLabel = this.imssRatesGrouped[0]?.group_label || '';
            },
            error: () => this.utils.alertSwal({ title: 'Error', text: 'No se pudieron cargar las cuotas de IMSS', icon: 'error' })
        });
    }

    toggleImssGroup(): void {
        this.imssGroupExpanded = !this.imssGroupExpanded;
    }

    // ---- Multiselects "Configurar faltas por tipo de descuento" ----

    toggleDropdown(field: string): void {
        this.openDropdown = this.openDropdown === field ? null : field;
    }

    isDropdownOpen(field: string): boolean {
        return this.openDropdown === field;
    }

    isDiscountTypeSelected(field: string, value: string): boolean {
        return (this.form.get(field)?.value || []).includes(value);
    }

    toggleDiscountType(field: string, value: string): void {
        const current: string[] = this.form.get(field)?.value || [];
        const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
        this.form.get(field)?.setValue(next);
    }

    selectedCountLabel(field: string): string {
        const count = (this.form.get(field)?.value || []).length;
        return count > 0 ? `${count} elemento(s) seleccionado(s)` : 'Elementos seleccionados';
    }

    setMonthlyFactorCalculation(value: string): void {
        this.form.get('monthly_factor_calculation')?.setValue(value);
    }

    save(): void {
        const payload = this.form.getRawValue();

        this.spinner.show();
        this.payrollConfigService.update(payload).subscribe({
            next: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'La configuración se guardó correctamente', icon: 'success' });
            },
            error: (err: any) => {
                this.spinner.hide();
                // ApiService.handleError() ya reduce el error HTTP a un string
                // plano (ver src/app/shared/api.service.ts) — err NO es un
                // HttpErrorResponse aquí, así que no hay err.error.message.
                const text = (typeof err === 'string' && err) || 'No se pudo guardar la configuración';
                this.utils.alertSwal({ title: 'Error', text, icon: 'error' });
            }
        });
    }
}
